/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { api, mutatingPost, type ApiResult } from "./client";
import { PAGE_SIZE, type Paged } from "./registry";
import type { SortChoice } from "../lib/sorting";

export const REGISTRY_SIGNAL_LABELS: Record<string, string> = {
  FAX_MATCH: "Same fax",
  EMAIL_MATCH: "Same email",
  PHONE_MATCH: "Same phone",
  DOMAIN_MATCH: "Same email domain",
  NAME_SIMILARITY: "Similar name",
};

export type RegistryEntitySide = {
  id: number;
  name: string | null;
};

export type RegistryDuplicateCase = {
  id: string;
  entityType: "ORG" | "PROVIDER";
  entityA: RegistryEntitySide;
  entityB: RegistryEntitySide;
  signals: string[];
  score: number;
  status: "OPEN" | "DISMISSED" | "MERGED";
  createdAt: string | null;
};


export function listRegistryCases(
  q: string,
  page: number,
  filter: string | null,
  sort: SortChoice,
): Promise<ApiResult<Paged<RegistryDuplicateCase>>> {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(PAGE_SIZE),
    sortBy: sort.sortBy,
    sortOrder: sort.sortOrder,
  });
  if (q.trim() !== "") {
    params.set("q", q.trim());
  }
  if (filter) {
    params.set("filter", filter);
  }
  return api(`/v1/referrals/duplicates?${params.toString()}`);
}

export function triggerRegistryScan(): Promise<ApiResult<{ status: string }>> {
  return api<{ status: string }>("/v1/referrals/duplicates/scan", mutatingPost({}));
}

export function dismissRegistryCase(
  id: string,
  note: string,
): Promise<ApiResult<{ status: string }>> {
  return api<{ status: string }>(
    `/v1/referrals/duplicates/${encodeURIComponent(id)}/dismiss`,
    mutatingPost({ note }),
  );
}

export type RegistryCaseSide = {
  id: number;
  name: string | null;
  email: string | null;
  fax: string | null;
  phone: string | null;
  state: string | null;
  domain: string | null;
  organization: string | null;
  createdAt: string | null;
  deleted: boolean;
};

export type RegistryCaseDetail = {
  id: string;
  entityType: "ORG" | "PROVIDER";
  entityA: RegistryCaseSide;
  entityB: RegistryCaseSide;
  signals: string[];
  score: number;
  status: "OPEN" | "DISMISSED" | "MERGED";
  survivorId: number | null;
  resolutionNote: string | null;
  createdAt: string | null;
};

export function getRegistryCase(id: string): Promise<ApiResult<RegistryCaseDetail>> {
  return api<RegistryCaseDetail>(`/v1/referrals/duplicates/${encodeURIComponent(id)}`);
}

export function confirmRegistryMerge(
  id: string,
  survivorId: number,
  note: string,
): Promise<ApiResult<RegistryCaseDetail>> {
  return api<RegistryCaseDetail>(
    `/v1/referrals/duplicates/${encodeURIComponent(id)}/confirm`,
    mutatingPost({ survivorId, note }),
  );
}
